import get from 'lodash/get';

import { ByIdStructure, DropDownOption } from '../types';

const getDataOptions = <T extends object>(data: Array<T> | ByIdStructure<T>) => {
    if (Array.isArray(data)) {
        return data;
    } else {
        return !data.ids?.length ? [] : data.ids.map((id) => data.byId[id]);
    }
};

const getDataNameAnd_id = (
    data: Array<any> | ByIdStructure<any>,
    name: string = 'name'
): Array<DropDownOption> => {
    if (Array.isArray(data)) {
        // Filter out non-object items (e.g., string IDs from unpopulated references)
        return data
            .filter((item) => item && typeof item === 'object' && item[name])
            .map((item) => ({ _id: item._id, name: item[name] }));
    }
    if (!data.ids?.length) return [];
    return data.ids
        .filter((id) => {
            const item = data.byId[id];
            return item && typeof item === 'object' && item[name];
        })
        .map((id) => ({ _id: data.byId[id]._id, name: data.byId[id][name] }));
};

const taytanSorter = (data: any[], key: string, desc?: boolean, id?: any) => {
    // testing
    const _sortByKeyTerm = (a: any, b: any) => {
        const aValue = get(a, key, null);
        const bValue = get(b, key, null);
        if (aValue < bValue) {
            return desc ? 1 : -1;
        }
        if (aValue > bValue) {
            return desc ? -1 : 1;
        }
        return 0;
    };

    if (!Array.isArray(data)) {
        return data;
    }
    if (id === 1) {
        // do something
    }
    return data.sort(_sortByKeyTerm);
};

type SortByKeyTermType = {
    desc?: boolean;
    funcIfTrueOrderLast?: (value: any) => boolean;
    funcToValue?: (value: any, key: string) => any;
};

const sortByKeyTerm = (key: string, options?: SortByKeyTermType) => (a: any, b: any) => {
    const { desc, funcIfTrueOrderLast, funcToValue = (val: any) => val } = options || {};

    const upSort = desc ? -1 : 1;
    const downSort = desc ? 1 : -1;

    const aValue = funcToValue(get(a, key, null), key);
    const bValue = funcToValue(get(b, key, null), key);

    if (funcIfTrueOrderLast) {
        if (funcIfTrueOrderLast(aValue) || funcIfTrueOrderLast(bValue)) {
            return 1;
        }
    }

    if (!aValue && !bValue) {
        return 0;
    }
    if (aValue < bValue) {
        if (!aValue && aValue !== 0) {
            return upSort;
        }
        return downSort;
    } else {
        if (!bValue && bValue !== 0) {
            return downSort;
        }
        return upSort;
    }
};
const sortByKeysTerm =
    (
        keys: string[],
        options?: {
            desc?: boolean;
            booleanCompareField?: string;
            funcToValue?: (value: any, key: string) => any;
        }
    ) =>
    (a: any, b: any) => {
        const { desc, funcToValue = (val: any) => val, booleanCompareField } = options || {};

        const idx = keys.findIndex((key) => get(a, key, null) !== get(b, key, null));
        if (idx === -1) {
            return 0;
        }
        const upSort = desc ? -1 : 1;
        const downSort = desc ? 1 : -1;

        if (booleanCompareField) {
            const aBool = !!get(a, booleanCompareField, null);
            const bBool = !!get(b, booleanCompareField, null);
            if (aBool !== bBool) {
                if (aBool < bBool) {
                    return upSort;
                } else {
                    return downSort;
                }
            }
        }

        const aValue = funcToValue(get(a, keys[idx], null), keys[idx]);
        const bValue = funcToValue(get(b, keys[idx], null), keys[idx]);
        if (aValue < bValue) {
            if (!aValue && aValue !== 0) {
                return upSort;
            }
            return downSort;
        } else {
            if (!bValue && bValue !== 0) {
                return downSort;
            }
            return upSort;
        }
    };

// Sorting helper functions:

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export {
    getDataOptions,
    getDataNameAnd_id,
    taytanSorter,
    sortByKeyTerm,
    sortByKeysTerm,
    Order,
    descendingComparator,
    getComparator,
    stableSort
};
